import { useSyncExternalStore } from 'react'

import { isLocal } from 'src/constants'

import type { FeatureGate } from './featureFlagging'

/**
 * Local, developer-only feature gate overrides. When an override is present for
 * a gate, `useFeatureFlagging` returns it instead of consulting Statsig. State
 * is persisted to localStorage so toggles survive refreshes, synced across tabs
 * via the `storage` event, and only applied in the local development environment
 * (see `areFeatureFlagOverridesEnabled`). An absent entry means "follow Statsig".
 */
export type FeatureFlagOverrides = Record<string, boolean>

const STORAGE_KEY = 'govwell:featureFlagOverrides'
const EMPTY_OVERRIDES: FeatureFlagOverrides = {}

/**
 * Overrides are a local debugging affordance, scoped to the `Local` environment
 * only. This matches the backend bridge (the Apollo link forwards overrides and
 * the backend honors them only in `Local`), so a panel toggle controls the
 * frontend and backend together everywhere the panel is available — no deployed
 * environment shows the panel or applies overrides. Gated here once so the
 * panel, the FE resolver, and the request-forwarding link all agree.
 */
export const areFeatureFlagOverridesEnabled = isLocal()

const readFromStorage = (): FeatureFlagOverrides => {
  if (typeof window === 'undefined') {
    return EMPTY_OVERRIDES
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return EMPTY_OVERRIDES
    }
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') {
      return EMPTY_OVERRIDES
    }
    // Tolerate stale/corrupt shapes by keeping only boolean entries.
    const result: FeatureFlagOverrides = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'boolean') {
        result[key] = value
      }
    }
    return result
  } catch {
    return EMPTY_OVERRIDES
  }
}

let cache: FeatureFlagOverrides = readFromStorage()
const subscribers = new Set<() => void>()

const notify = (): void => {
  for (const subscriber of subscribers) {
    subscriber()
  }
}

const handleStorageEvent = (event: StorageEvent): void => {
  if (event.key !== STORAGE_KEY) {
    return
  }
  cache = readFromStorage()
  notify()
}

const subscribe = (callback: () => void): (() => void) => {
  if (typeof window !== 'undefined' && subscribers.size === 0) {
    window.addEventListener('storage', handleStorageEvent)
  }
  subscribers.add(callback)
  return () => {
    subscribers.delete(callback)
    if (typeof window !== 'undefined' && subscribers.size === 0) {
      window.removeEventListener('storage', handleStorageEvent)
    }
  }
}

// useSyncExternalStore requires a referentially stable snapshot; `cache` is only
// reassigned when the override map actually changes.
const getSnapshot = (): FeatureFlagOverrides => cache
const getServerSnapshot = (): FeatureFlagOverrides => EMPTY_OVERRIDES

const persist = (next: FeatureFlagOverrides): void => {
  cache = next
  if (typeof window !== 'undefined') {
    try {
      if (Object.keys(next).length === 0) {
        window.localStorage.removeItem(STORAGE_KEY)
      } else {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      }
    } catch {
      // Storage can fail (private mode, quota); keep the in-memory override.
    }
  }
  notify()
}

/** Subscribe to the current override map; re-renders on any override change. */
export const useFeatureFlagOverrides = (): FeatureFlagOverrides =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

/**
 * Current override map for non-React readers (e.g. an Apollo link that forwards
 * overrides to the backend on each request). Not reactive — reads the latest
 * persisted snapshot at call time.
 */
export const getFeatureFlagOverridesSnapshot = (): FeatureFlagOverrides => cache

/**
 * Force a gate on/off, or pass `null` to clear the override and follow Statsig.
 * Accepts any gate string (not just the FE enum) so the dev panel can override
 * backend-only gates, which are forwarded to the backend on each request.
 */
export const setFeatureFlagOverride = (gate: FeatureGate | string, value: boolean | null): void => {
  const next = { ...cache }
  if (value === null) {
    delete next[gate]
  } else {
    next[gate] = value
  }
  persist(next)
}

export const clearFeatureFlagOverrides = (): void => {
  persist({})
}
